import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { createCharge, getBookingBalance, spendBookingPack, type CreateChargeInput } from '../api/endpoints/charges';
import { createInvoiceForCharge } from '../api/endpoints/invoices';
import { listBookings } from '../api/endpoints/bookings';
import { listOffers } from '../api/endpoints/offers';
import { listServices } from '../api/endpoints/services';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import { createMutationAttempt, isDefiniteMutationFailure } from '../lib/mutation-attempt';
import type { Booking, BusinessOffer, BusinessServiceRecord, ChargeCreated, ChargePaymentMethod, InvoiceRecord } from '../api/schemas';
import { ChargeBookingPicker } from '../components/ChargeBookingPicker';
import { ChargeCart } from '../components/ChargeCart';
import { ChargeCatalogGrid } from '../components/ChargeCatalogGrid';
import { ChargeDiscountPanel, type DiscountSelection } from '../components/ChargeDiscountPanel';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';
import { InvoiceMailer } from '../components/InvoiceMail';
import { Notice } from '../components/Notice';
import { PageHeader } from '../components/PageHeader';
import { bookingsToCharge } from '../lib/bookings';
import { bookingsQueryKey, chargeWindow } from '../lib/bookingWindows';
import { customerNameOf } from '../lib/bookingPresentation';
import { BOOKING_BALANCE_QUERY_KEY, bookingPaymentsNotice, packCreditsLine, type BookingPaymentsRead } from '../lib/bookingPayments';
import {
  addFreeformLine,
  addServiceLine,
  currentOffers,
  linesAfterPackUse,
  linesForLinkedBooking,
  reconcileWithCatalog,
  setLineQuantity,
  type CartLine,
} from '../lib/cart';
import { computeChargeTotals, type ChargeDiscountCalc } from '../lib/chargeTotals';
import { formatMoneyCents, formatTime } from '../lib/format';
import { modeForCategory } from '../lib/mode';
import { autoInvoiceNote, offersManualInvoice } from '../lib/autoInvoice';
import {
  PAYMENT_METHODS,
  PAYMENT_REFERENCE_MAX,
  paymentMethodLabel,
  paymentMethodWithReference,
  paymentReferenceError,
  requiresPaymentReference,
} from '../lib/paymentMethods';
import { serviceName, useBusiness } from '../lib/useBusiness';
import { useCapability } from '../lib/useCapability';
import { usePermissions } from '../auth/usePermissions';
import { Money } from '../components/Figures';

const SERVICES_ACTIVE_QUERY_KEY = ['business-services-active'];
const OFFERS_QUERY_KEY = ['business-offers'];

type Step = 'cobrar' | 'confirmar' | 'cobrado';

type MismatchNotice = {
  message: string;
  droppedNames: string[];
};

/**
 * Cobrar → Confirmar → Cobrado (docs/FLOWS.md §1). The cart is transient UI
 * state, never persisted (docs/CONCEPTS.md — Cobro): a session refresh or a
 * 401 loses it by design.
 */
type CobrarPageProps = {
  /** Arrives from a booking's Cobrar button: that visit is linked and its service is in the cart. */
  initialBookingId?: string;
};

export function CobrarPage({ initialBookingId }: CobrarPageProps = {}) {
  const business = useBusiness();
  const restaurantMode = business ? modeForCategory(business.category) === 'restaurant' : false;
  const catalogLabel = restaurantMode ? 'Menú' : 'Servicios';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canInvoice = usePermissions().can('facturas');
  const packsOpen = useCapability('planes');
  const servicesQuery = useQuery({
    queryKey: SERVICES_ACTIVE_QUERY_KEY,
    queryFn: () => listServices(false),
  });
  const offersQuery = useQuery({ queryKey: OFFERS_QUERY_KEY, queryFn: listOffers });
  const bookingsRange = chargeWindow(new Date());
  const bookingsQuery = useQuery({
    queryKey: bookingsQueryKey(bookingsRange),
    queryFn: () => listBookings(bookingsRange),
  });

  const [step, setStep] = useState<Step>('cobrar');
  const [lines, setLines] = useState<CartLine[]>([]);
  const [discount, setDiscount] = useState<DiscountSelection | undefined>();
  const [paymentMethod, setPaymentMethod] = useState<ChargePaymentMethod>('efectivo');
  const [paymentReference, setPaymentReference] = useState('');
  const [referenceError, setReferenceError] = useState<string | undefined>();
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [mismatchNotice, setMismatchNotice] = useState<MismatchNotice | undefined>();
  const [lastCharge, setLastCharge] = useState<ChargeCreated | undefined>();
  const [paymentsRead, setPaymentsRead] = useState<BookingPaymentsRead | undefined>();
  const selectedBookingId = useRef<string | null>(null);

  const chargeAttempt = useRef(createMutationAttempt<CreateChargeInput>()).current;

  const services = servicesQuery.data?.data ?? [];
  const offers = currentOffers(offersQuery.data?.data ?? [], new Date());
  const bookingsOpen = useMemo(
    () => bookingsToCharge(bookingsQuery.data?.data ?? [], new Date()),
    [bookingsQuery.data],
  );
  const linkedBooking = bookingsOpen.find((booking) => booking.id === bookingId);

  // Whoever runs the counter reads what the visit already received, so a
  // partly paid one brings only its saldo. The server also refuses a service
  // charged twice.
  async function readPayments(id: string): Promise<BookingPaymentsRead> {
    try {
      const balance = await queryClient.fetchQuery({
        queryKey: [BOOKING_BALANCE_QUERY_KEY, id],
        queryFn: () => getBookingBalance(id),
        staleTime: 0,
      });
      return { balance, failed: false };
    } catch {
      return { failed: true };
    }
  }

  // A visit brings what it still owes: its service, only its saldo, or nothing.
  async function handleSelectBooking(booking: Booking | null) {
    selectedBookingId.current = booking?.id ?? null;
    setBookingId(booking?.id ?? null);
    setPaymentsRead(undefined);
    setLines((prev) => linesForLinkedBooking(prev, null));
    if (!booking) return;
    const read = await readPayments(booking.id);
    if (selectedBookingId.current !== booking.id) return;
    const service = services.find((entry) => entry.id === booking.serviceId);
    setPaymentsRead(read);
    setLines((prev) =>
      linesForLinkedBooking(prev, {
        bookingId: booking.id,
        service,
        serviceName: service?.name ?? serviceName(business, booking.serviceId),
        ...(read.balance ? { balance: read.balance } : {}),
      }),
    );
  }

  const preselected = useRef(false);
  useEffect(() => {
    if (preselected.current || !initialBookingId || !servicesQuery.data || !bookingsQuery.data) return;
    preselected.current = true;
    const booking = bookingsOpen.find((entry) => entry.id === initialBookingId);
    if (booking) void handleSelectBooking(booking);
    // handleSelectBooking reads the same loaded lists; running it once is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBookingId, servicesQuery.data, bookingsQuery.data, bookingsOpen]);

  // Usar paquete: a credit of the client's package pays the visit, so its
  // service leaves the cuenta and only what was added is left to charge. Each
  // visit keeps its own Idempotency-Key: a retry after a lost answer reuses it,
  // so it can never spend twice, and another visit never inherits it.
  const packKeys = useRef(new Map<string, string>()).current;
  const packMutation = useMutation({
    meta: { exito: 'Paquete usado' },
    mutationFn: (id: string) => {
      const key = packKeys.get(id) ?? crypto.randomUUID();
      packKeys.set(id, key);
      return spendBookingPack(id, key);
    },
    onSuccess: (balance, id) => {
      packKeys.delete(id);
      queryClient.setQueryData([BOOKING_BALANCE_QUERY_KEY, id], balance);
      void queryClient.invalidateQueries({ queryKey: ['customer-subscriptions'] });
      void queryClient.invalidateQueries({ queryKey: ['business-bookings'] });
      if (selectedBookingId.current !== id) return;
      setPaymentsRead({ balance, failed: false });
      const visit = bookingsOpen.find((entry) => entry.id === id);
      if (visit) setLines((prev) => linesAfterPackUse(prev, visit.serviceId));
    },
    // A definite refusal is final, so the next try is a new request; a lost answer keeps the key for the retry.
    onError: (error, id) => {
      if (isDefiniteMutationFailure(error)) packKeys.delete(id);
    },
  });
  const packOffer = packsOpen && bookingId ? paymentsRead?.balance?.packAvailable ?? null : null;

  const totals = computeChargeTotals(lines, toDiscountCalc(discount));
  const paymentsNotice = bookingPaymentsNotice(paymentsRead);

  function handleAddService(service: BusinessServiceRecord) {
    setLines((prev) => addServiceLine(prev, service));
  }

  function handleAddFreeform(name: string, unitPriceCents: number) {
    setLines((prev) => addFreeformLine(prev, name, unitPriceCents));
  }

  function handleSetQuantity(lineId: string, quantity: number) {
    setLines((prev) => setLineQuantity(prev, lineId, quantity));
  }

  function handleApplyAdhocDiscount(type: 'percent' | 'fixed', value: number) {
    setDiscount({ source: 'adhoc', type, value });
  }

  function handleApplyOffer(offer: BusinessOffer) {
    setDiscount({ source: 'offer', offerId: offer.id, title: offer.title, discount: offer.discount });
  }

  function handleClearDiscount() {
    setDiscount(undefined);
  }

  function handleSelectMethod(method: ChargePaymentMethod) {
    setPaymentMethod(method);
    setReferenceError(undefined);
  }

  function handleCobrarClick() {
    if (lines.length === 0) return;
    const problem = paymentReferenceError(paymentMethod, paymentReference);
    setReferenceError(problem);
    if (problem) return;
    setMismatchNotice(undefined);
    setStep('confirmar');
  }

  function handleVolver() {
    if (chargeAttempt.isUncertain()) return;
    chargeAttempt.complete();
    setStep('cobrar');
  }

  function handleNuevoCobro() {
    setLines([]);
    setDiscount(undefined);
    setPaymentMethod('efectivo');
    setPaymentReference('');
    setReferenceError(undefined);
    setBookingId(null);
    selectedBookingId.current = null;
    setPaymentsRead(undefined);
    setMismatchNotice(undefined);
    setLastCharge(undefined);
    setStep('cobrar');
  }

  async function recoverFromMismatch(message: string) {
    const refetched = await servicesQuery.refetch();
    const activeServices = refetched.data?.data ?? [];
    const { lines: reconciled, droppedNames } = reconcileWithCatalog(lines, activeServices);
    setLines(reconciled);
    setMismatchNotice({ message, droppedNames });
    setStep('cobrar');
  }

  const invoiceMutation = useMutationAttempt({
    mutationFn: (chargeId: string, key) => createInvoiceForCharge(chargeId, {}, key),
    onSuccess: (invoice) => {
      void navigate({ to: '/facturas/$invoiceId/imprimir', params: { invoiceId: invoice.id } });
    },
  });

  const reference = requiresPaymentReference(paymentMethod) ? paymentReference.trim() : undefined;
  const autoInvoice = lastCharge?.invoice;

  function printInvoice(invoice: InvoiceRecord) {
    // Without `facturas` the panel's own print page is closed, so the counter
    // prints the customer's public sheet, which is the same document.
    void navigate(
      canInvoice
        ? { to: '/facturas/$invoiceId/imprimir', params: { invoiceId: invoice.id } }
        : { to: '/factura/$shareToken', params: { shareToken: invoice.shareToken } },
    );
  }

  const chargeMutation = useMutation({
    mutationFn: () => {
      const original = chargeAttempt.begin(
        buildChargeInput(lines, discount, { method: paymentMethod, reference }, bookingId, totals.totalCents),
      );
      return createCharge(original.input, original.key);
    },
    onSuccess: (charge) => {
      chargeAttempt.complete();
      if (charge.bookingId) {
        void queryClient.invalidateQueries({ queryKey: [BOOKING_BALANCE_QUERY_KEY, charge.bookingId] });
      }
      setLastCharge(charge);
      setStep('cobrado');
    },
    onError: (error: unknown) => {
      chargeAttempt.fail(error);
      if (chargeAttempt.isUncertain() || !(error instanceof ApiError)) return;
      if (error.code === 'total_mismatch') void recoverFromMismatch(error.message);
    },
  });

  return (
    <main>
      <PageHeader title="Cobrar" description="Cóbrale al cliente que tienes al frente." />

      {step === 'cobrar' ? (
        <>
          {mismatchNotice ? (
            <>
              <ErrorBanner message={mismatchNotice.message} />
              {mismatchNotice.droppedNames.length > 0 ? (
                <Notice>
                  Quitamos del carrito: {mismatchNotice.droppedNames.join(', ')} (ya no disponibles).
                </Notice>
              ) : null}
            </>
          ) : null}

          <div className="pos">
            <section aria-label={catalogLabel}>
              <h2 className="sr-only">{catalogLabel}</h2>
              <ChargeCatalogGrid
                services={services}
                catalogLabel={catalogLabel}
                isLoading={servicesQuery.isLoading}
                isError={servicesQuery.isError}
                errorMessage={errorMessage(servicesQuery.error)}
                onAddService={handleAddService}
                onAddFreeform={handleAddFreeform}
              />
            </section>

            <aside className="pos-pane" aria-label="La cuenta">
              <h2 className="pos-pane-label">La cuenta</h2>
              <ChargeCart
                lines={lines}
                onSetQuantity={handleSetQuantity}
                subtotalCents={totals.subtotalCents}
                discountCents={totals.discountCents}
                discountLabel={discountLabel(discount)}
                totalCents={totals.totalCents}
                emptyHint={
                  restaurantMode
                    ? 'Toca un plato del menú para empezar la cuenta.'
                    : 'Toca un servicio para empezar la cuenta.'
                }
                beforeTotals={
                  <ChargeDiscountPanel
                    discount={discount}
                    offers={offers}
                    onApplyAdhoc={handleApplyAdhocDiscount}
                    onApplyOffer={handleApplyOffer}
                    onClear={handleClearDiscount}
                  />
                }
              />

              <ChargeBookingPicker
                bookings={bookingsOpen}
                business={business}
                selectedBookingId={bookingId}
                onSelect={(booking) => void handleSelectBooking(booking)}
              />
              {packOffer && bookingId ? (
                <section className="pos-pack" aria-label="Paquete del cliente">
                  <div className="pos-pack-row">
                    <span className="pos-pack-text">
                      <span className="pos-pack-name">{packOffer.planName}</span>
                      <span className="row-sub">{packCreditsLine(packOffer.creditsLeft)}</span>
                    </span>
                    <button
                      type="button"
                      className="btn secondary"
                      disabled={packMutation.isPending}
                      onClick={() => packMutation.mutate(bookingId)}
                    >
                      {packMutation.isPending ? 'Usando…' : 'Usar paquete'}
                    </button>
                  </div>
                  {packMutation.isError && packMutation.variables === bookingId ? (
                    <ErrorBanner message={errorMessage(packMutation.error)} />
                  ) : null}
                </section>
              ) : null}
              {paymentsNotice ? <Notice>{paymentsNotice}</Notice> : null}

              <div>
                <p className="pos-pane-label">Método de pago</p>
                <div className="seg" role="radiogroup" aria-label="Método de pago" style={{ marginTop: 8 }}>
                  {PAYMENT_METHODS.map((method) => (
                    <button
                      key={method.value}
                      type="button"
                      aria-pressed={paymentMethod === method.value}
                      onClick={() => handleSelectMethod(method.value)}
                    >
                      {method.label}
                    </button>
                  ))}
                </div>
              </div>

              {requiresPaymentReference(paymentMethod) ? (
                <FormField
                  label="Referencia"
                  htmlFor="cobrar-referencia"
                  error={referenceError}
                  hint="El número del comprobante que te mostraron."
                >
                  <input
                    id="cobrar-referencia"
                    value={paymentReference}
                    maxLength={PAYMENT_REFERENCE_MAX}
                    autoComplete="off"
                    required
                    onChange={(event) => {
                      setPaymentReference(event.target.value);
                      setReferenceError(undefined);
                    }}
                  />
                </FormField>
              ) : null}

              <button
                type="button"
                className="btn"
                disabled={lines.length === 0}
                onClick={handleCobrarClick}
              >
                Cobrar <Money cents={totals.totalCents} />
              </button>
            </aside>
          </div>
        </>
      ) : null}

      {step === 'confirmar' ? (
        <section className="pos-confirm" aria-label="Confirmar cobro">
          <h2 className="pos-pane-label">Confirmar</h2>
          <ChargeCart
            lines={lines}
            onSetQuantity={handleSetQuantity}
            subtotalCents={totals.subtotalCents}
            discountCents={totals.discountCents}
            discountLabel={discountLabel(discount)}
            totalCents={totals.totalCents}
            readOnly
          />
          <div>
            <p className="meta">Método: {paymentMethodLabel(paymentMethod)}</p>
            {reference ? <p className="meta">Referencia: {reference}</p> : null}
            {linkedBooking ? (
              <p className="meta">
                Reserva vinculada: {customerNameOf(linkedBooking) ?? 'Sin nombre'} ·{' '}
                {formatTime(linkedBooking.startsAt)} · {serviceName(business, linkedBooking.serviceId)}
              </p>
            ) : null}
          </div>
          {chargeMutation.isError && !isTotalMismatch(chargeMutation.error) ? (
            <ErrorBanner message={errorMessage(chargeMutation.error)} />
          ) : null}
          <span className="action-pair">
            <button
              type="button"
              className="btn"
              disabled={chargeMutation.isPending}
              onClick={() => chargeMutation.mutate()}
            >
              {chargeMutation.isPending ? 'Cobrando…' : 'Confirmar cobro'}
            </button>
            <button
              type="button"
              className="btn secondary"
              disabled={chargeMutation.isPending || chargeAttempt.isUncertain()}
              onClick={handleVolver}
            >
              Volver
            </button>
          </span>
        </section>
      ) : null}

      {step === 'cobrado' && lastCharge ? (
        <section className="pos-done" aria-label="Cobrado">
          <h2 className="status" data-tone="good">
            Cobrado
          </h2>
          <p className="amount">
            <Money cents={lastCharge.totalCents} rollIn />
          </p>
          <p className="meta">
            {paymentMethodWithReference(lastCharge.paymentMethod, lastCharge.paymentReference)} ·{' '}
            {formatTime(lastCharge.chargedAt)}
          </p>
          {autoInvoice ? <p className="meta">Factura {autoInvoice.displayNumber} emitida</p> : null}
          {lastCharge.invoiceSkipped ? (
            <p className="meta">{autoInvoiceNote(lastCharge.invoiceSkipped, canInvoice)}</p>
          ) : null}
          {invoiceMutation.isError ? (
            <ErrorBanner message={errorMessage(invoiceMutation.error)} />
          ) : null}
          <span className="action-pair">
            <button type="button" className="btn" disabled={invoiceMutation.isPending || invoiceMutation.uncertain} onClick={handleNuevoCobro}>
              Nuevo cobro
            </button>
            {autoInvoice ? (
              <button type="button" className="btn secondary" onClick={() => printInvoice(autoInvoice)}>
                Imprimir
              </button>
            ) : null}
            {canInvoice && offersManualInvoice(lastCharge) ? (
              <button
                type="button"
                className="btn secondary"
                disabled={invoiceMutation.isPending}
                onClick={() => invoiceMutation.uncertain ? invoiceMutation.retry() : invoiceMutation.mutate(lastCharge.id)}
              >
                {invoiceMutation.isPending ? 'Emitiendo…' : 'Emitir factura'}
              </button>
            ) : null}
          </span>
          {autoInvoice && canInvoice ? <InvoiceMailer invoiceId={autoInvoice.id} /> : null}
        </section>
      ) : null}
    </main>
  );
}

function toDiscountCalc(discount: DiscountSelection | undefined): ChargeDiscountCalc | undefined {
  if (!discount) return undefined;
  if (discount.source === 'adhoc') return { type: discount.type, value: discount.value };
  return discount.discount.type === 'percent'
    ? { type: 'percent', value: discount.discount.percent }
    : { type: 'fixed', value: discount.discount.amountCents };
}

function discountLabel(discount: DiscountSelection | undefined): string | undefined {
  if (!discount) return undefined;
  if (discount.source === 'offer') {
    return discount.discount.type === 'percent'
      ? `${discount.discount.percent}%`
      : formatMoneyCents(discount.discount.amountCents);
  }
  return discount.type === 'percent' ? `${discount.value}%` : formatMoneyCents(discount.value);
}

function buildChargeInput(
  lines: CartLine[],
  discount: DiscountSelection | undefined,
  payment: { method: ChargePaymentMethod; reference: string | undefined },
  bookingId: string | null,
  expectedTotalCents: number,
): CreateChargeInput {
  const items: CreateChargeInput['items'] = lines.map((line) =>
    line.serviceId
      ? { serviceId: line.serviceId, quantity: line.quantity }
      : { name: line.name, unitPriceCents: line.unitPriceCents, quantity: line.quantity },
  );
  return {
    items,
    discount: discount?.source === 'adhoc' ? { type: discount.type, value: discount.value } : undefined,
    offerId: discount?.source === 'offer' ? discount.offerId : undefined,
    paymentMethod: payment.method,
    // Only a method that asks for it carries one: a reference typed for a
    // transferencia never rides along on the efectivo chosen after it.
    ...(payment.reference ? { paymentReference: payment.reference } : {}),
    bookingId: bookingId ?? undefined,
    expectedTotalCents,
  };
}

function isTotalMismatch(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'total_mismatch';
}

function errorMessage(error: unknown): string {
  // The server names the reference field when it refuses it; that line says more than "revisa los datos".
  if (error instanceof ApiError) return error.fields?.paymentReference ?? error.message;
  return 'No pudimos completar eso ahora mismo.';
}
